/**
 * Curated set of color-cast corrections for known AI image generators.
 * Each entry maps a model-name pattern (case-insensitive substring) to
 * a partial grade object. Apply via the existing applyLook() pipeline.
 * Values are conservative — designed to neutralize the bias without
 * erasing model character. Tunable per user via the standard grade
 * controls after apply.
 *
 * Phase 0 hardening (Codex GO Phase 0.harden): migrated from
 * diffusion-casts.json to a plain ESM JS data module. Avoids
 * `import … with { type: 'json' }` attribute-syntax fragility across
 * Vite 5.4, esbuild 0.21, and Node 22+ ESM loaders.
 *
 * @module src/pipeline/diffusion-casts
 */
export const version = 1;

export const casts = [
    {
        id: 'sdxl',
        name: 'SDXL warm-cast neutral',
        matchPatterns: ['sd_xl', 'sdxl', 'stable-diffusion-xl'],
        description: "Compensates SDXL's warm/orange shift + crushed shadow blacks.",
        grade: {
            liftR:           -0.008,
            liftB:            0.005,
            liftG:            0.001,
            gammaR:           0.000,
            gammaG:           0.000,
            gammaB:           0.000,
            gradeShadows:     0.06,
            gradeSaturation:  48,
        },
    },
    {
        id: 'flux',
        name: 'Flux cyan-cast neutral',
        matchPatterns: ['flux', 'flux1', 'flux.1', 'flux-dev', 'flux-schnell'],
        description: "Compensates Flux's cyan/cool tint + lifted blacks.",
        grade: {
            liftR:           0.005,
            liftB:          -0.006,
            liftG:           0.000,
            gradeShadows:   -0.04,
            gradeContrast:   1.04,
        },
    },
    {
        id: 'mj-v6',
        name: 'Midjourney v6 contrast neutral',
        matchPatterns: ['midjourney', 'mj v6', 'mj-v6', 'mj_v6'],
        description: "Reduces MJ v6's heavy contrast crunch + magenta shadows.",
        grade: {
            liftG:           0.003,
            liftR:          -0.003,
            gradeContrast:   0.96,
            gradeShadows:    0.08,
        },
    },
    {
        id: 'sd15',
        name: 'SD 1.5 green-magenta drift neutral',
        matchPatterns: ['sd 1.5', 'sd1.5', 'sd-1.5', 'sd_1_5', 'v1-5', 'v1.5'],
        description: 'Reduces midtone green-magenta drift typical of SD 1.5 finetunes.',
        grade: {
            gammaG:          -0.005,
            gammaR:           0.002,
            gradeSaturation:  47,
        },
    },
    {
        id: 'dalle3',
        name: 'DALL-E 3 warm-yellow neutral',
        matchPatterns: ['dall-e', 'dalle', 'dall_e'],
        description: "Compensates DALL-E 3's yellow/warm cast.",
        grade: {
            liftR:           -0.006,
            liftG:           -0.003,
            liftB:            0.005,
            gradeSaturation:  48,
        },
    },
    {
        id: 'comfyui-generic',
        name: 'ComfyUI generic neutral',
        matchPatterns: ['comfyui'],
        description: 'Mild neutral correction when only ComfyUI is detected without specific model info.',
        grade: {
            liftR:        -0.003,
            liftB:         0.002,
            gradeShadows:  0.03,
        },
    },
];

export default { version, casts };
